import * as pdfjsLib from 'pdfjs-dist';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Align, VariableSizeList } from 'react-window';
import { useAppDispatch } from 'store/hook';
import { setIsDocumentError } from 'store/slices/viewerSlice';
import { useResizeObserver } from 'use-resize-observer';
import Toolbar from '../Toolbar';
import classes from '../Viewer.module.scss';
import Viewer from './Viewer';

const getOnRangeNum = (number: number, range: number) => {
  if (number > range) return range;
  if (number < 1) return 1;
  return number;
};

interface PDFViewerProps {
  url?: any;
  data64?: string;
  isModal?: boolean;
  setIsAvailableAccept?: React.Dispatch<React.SetStateAction<boolean>>;
  onClose?: () => void;
  downloadDocument?: () => void;
  isMobile?: boolean;
  isStatic?: boolean;
  defaultScale?: number;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  url,
  data64,
  onClose,
  isModal,
  isStatic,
  setIsAvailableAccept,
  downloadDocument,
  isMobile = false,
  defaultScale = 1
}) => {
  const defaultZoom = isMobile ? 0.75 : defaultScale;
  const [pagesCount, setPagesCount] = React.useState(0);
  const [pages, setPages] = React.useState<pdfjsLib.PDFPageProxy[]>([]);
  const [scale, setScale] = React.useState<number>(defaultZoom);
  const [currentPage, setCurrentPage] = React.useState(1);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const pdfRef = React.useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const windowRef = React.useRef<VariableSizeList | null>(null);

  const { ref: toolbarRef, height = 0 } = useResizeObserver();
  const { ref: wrapRef, width: internalWidth = 400, height: internalHeight = 600 } = useResizeObserver();

  const actionsKeydown = {
    PageDown: () => scrollToItem(currentPage + 1),
    PageUp: () => scrollToItem(currentPage - 1),
    Home: () => scrollToItem(1),
    End: () => scrollToItem(pages.length, 'end'),
  };

  const keydownListener = (e: globalThis.KeyboardEvent) => {
    ['End','Home','PageUp','PageDown'].includes(e.code) && actionsKeydown[e.code as keyof typeof actionsKeydown]()
  };

  React.useEffect(() => {
    document.addEventListener('keydown', keydownListener);
    return () => {
      document.removeEventListener('keydown', keydownListener);
    };
  }, [currentPage, pages.length]);

  React.useEffect(() => {
    renderPage();
  }, [url]);

  React.useEffect(() => {
    windowRef.current?.scrollToItem(currentPage - 1, 'start');
  }, [scale]);

  React.useEffect(() => {
    if (pages.length > 0 && pages.length < pagesCount) {
      getRestOfPages();
    }
  }, [pages.length, pagesCount]);

  const getRestOfPages = async () => {
    let arrWithPages: pdfjsLib.PDFPageProxy[] = pages;
    for (let i = pages.length; i < pagesCount; i++) {
      const page = await pdfRef.current!.getPage(i + 1);
      arrWithPages.push(page);
    }
    setPages(arrWithPages);
  };

  const renderPage = async () => {
    try {
      const data = atob(data64 || '');
      const pdf = url ? await pdfjsLib.getDocument(url).promise : await pdfjsLib.getDocument({ data }).promise;
      pdfRef.current = pdf;
      setPagesCount(pdf?._pdfInfo.numPages);

      // Fetch the first page
      const pageNumber = 1;
      await pdf?.getPage(pageNumber);
    } catch (err) {
      dispatch(setIsDocumentError(t('Documents.error.unSupportedView')))
    }
  };

  const handleGetPdfPage = React.useCallback((index: any) => {
    return pdfRef.current!.getPage(index + 1);
  }, []);
  
  const scrollToItem = (value: number | null, position?: Align) => {
    const result = getOnRangeNum(value!, pagesCount);
    setCurrentPage && setCurrentPage(result);
    windowRef.current && windowRef.current.scrollToItem(result - 1, position || 'start');
  };

  return (
    <div ref={wrapRef} className={isStatic ? classes.page_static : classes.page_overlay}>
      <Toolbar
        scale={scale}
        setScale={setScale}
        pagesCount={pagesCount}
        toolbarRef={toolbarRef}
        onClose={onClose}
        scrollToItem={scrollToItem}
        currentPage={currentPage}
        downloadDocument={downloadDocument}
        isModal={isModal}
        isMobile={isMobile}
        isStatic={isStatic}
      />
      <Viewer
        pages={pages}
        setPages={setPages}
        pagesCount={pagesCount}
        scale={scale}
        setScale={setScale}
        handleGetPdfPage={handleGetPdfPage}
        windowRef={windowRef}
        height={internalHeight - height}
        width={internalWidth}
        setIsAvailableAccept={setIsAvailableAccept}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export default PDFViewer;
